import { z } from 'zod';

/**
 * Lock of an invariant. Tahiti has three modes (`rose_invariant_lock_mode.code`): `preserve_exact`
 * is "strict" (reproduced as it is); `translate_controlled` and `format_controlled` are "souple"
 * (the element may be adapted: only `preserve_exact` is reproduced as it is).
 */
export const verrouInvariantSchema = z.enum(['strict', 'souple']);
export type VerrouInvariant = z.infer<typeof verrouInvariantSchema>;

/** An element of the text that the translation must keep (a name, a figure, a quote, an address...). */
export const invariantSchema = z.object({
  /** The element as it is written in the text of origin. */
  valeur: z.string().min(1),
  /** The kind of element, in words ("nom", "adresse web"), as Tahiti labels it. */
  type: z.string().min(1),
  verrou: verrouInvariantSchema,
});
export type Invariant = z.infer<typeof invariantSchema>;

/** The purpose of the text, as the analysis step of Tahiti gives it: never written by Rosetta. */
export const finaliteSchema = z.object({
  /** `result_json.output_json.finalite`: "demande", "annonce"... */
  libelle: z.string().min(1),
  /** `result_json.output_json.finalite_explication`, when Tahiti gives one. */
  explication: z.string().min(1).optional(),
});
export type Finalite = z.infer<typeof finaliteSchema>;

/**
 * Who speaks, the kind of text, its tone and its objective, as the analysis step of Tahiti words
 * them (#10, the "Texte d'origine" tab): `result_json.output_json` of `rose_task_v5_analyse`,
 * copied word for word. Tahiti has no separate field for the addressee: it is in `voix`. A
 * field Tahiti did not give is absent.
 */
export const profilTexteSchema = z.object({
  /** `voix`: free text, who speaks and to whom. */
  voix: z.string().min(1).optional(),
  /** `genre`: "message", "courriel"... */
  genre: z.string().min(1).optional(),
  /** `ton`: "familier, détendu, enjoué"... */
  ton: z.string().min(1).optional(),
  /** `objectif.formulation`. */
  objectif: z.string().min(1).optional(),
});
export type ProfilTexte = z.infer<typeof profilTexteSchema>;

/**
 * What the analysis of a translation tells about its text (#9, #10). Each part is read on its
 * own: `finalite` and `profil` are `null` when the analysis step has no readable answer (or, for
 * `profil`, none of its four fields), `invariants` is `null` when the invariants step has none
 * (an empty list is a step that found nothing).
 */
export const analyseTexteSchema = z.object({
  finalite: finaliteSchema.nullable(),
  profil: profilTexteSchema.nullable(),
  invariants: z.array(invariantSchema).nullable(),
});
export type AnalyseTexte = z.infer<typeof analyseTexteSchema>;
